import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Plus, Pencil, Trash2, Loader2, Link as LinkIcon, ExternalLink, Building2,
} from "lucide-react";

const API = getApiUrl();

const PROPERTY_PORTALS = [
  {
    id: "rightmove",
    name: "Rightmove",
    description: "The UK's largest property portal. Sync your listings directly to Rightmove for maximum buyer reach.",
    color: "bg-emerald-50 border-emerald-200 text-emerald-900",
    badge: "bg-emerald-100 text-emerald-700",
  },
  {
    id: "zoopla",
    name: "Zoopla",
    description: "Major UK property portal. Push your listings to Zoopla to attract additional buyers.",
    color: "bg-purple-50 border-purple-200 text-purple-900",
    badge: "bg-purple-100 text-purple-700",
  },
];

export default function AdminIntegrationsTab() {
  const [hyperlinks, setHyperlinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    url: "",
    open_in_new_tab: true,
    order: 0,
  });

  useEffect(() => { fetchHyperlinks(); }, []);

  const fetchHyperlinks = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/integration-hyperlinks`, { withCredentials: true });
      setHyperlinks(r.data?.hyperlinks || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load hyperlinks");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData({ title: "", url: "", open_in_new_tab: true, order: hyperlinks.length });
    setShowDialog(true);
  };

  const openEdit = (link) => {
    setEditing(link);
    setFormData({
      title: link.title || "",
      url: link.url || "",
      open_in_new_tab: link.open_in_new_tab !== false,
      order: link.order || 0,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.url.trim()) {
      toast.error("Title and URL are required");
      return;
    }
    try {
      setSaving(true);
      if (editing) {
        await axios.put(
          `${API}/admin/integration-hyperlinks/${editing.link_id}`,
          formData,
          { withCredentials: true }
        );
        toast.success("Hyperlink updated");
      } else {
        await axios.post(
          `${API}/admin/integration-hyperlinks`,
          formData,
          { withCredentials: true }
        );
        toast.success("Hyperlink created");
      }
      setShowDialog(false);
      fetchHyperlinks();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (link) => {
    if (!confirm(`Delete hyperlink "${link.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/integration-hyperlinks/${link.link_id}`, {
        withCredentials: true,
      });
      toast.success("Hyperlink deleted");
      fetchHyperlinks();
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-integrations-tab">
      <div>
        <h2 className="text-2xl font-bold">Links</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Manage external links shown on your site. Rightmove, Zoopla, Rendy, WhatsApp,
          Resend, Ideal Postcodes, and Vosa now live under the main "Integrations" tab.
        </p>
      </div>

      {/* Hyperlinks CRUD */}
      <section className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-lg font-semibold">Hyperlinks</h3>
            <p className="text-sm text-muted-foreground">
              Manage external links you want to surface on your site (e.g., partner sites, social profiles, third-party tools).
            </p>
          </div>
          <Button onClick={openCreate} data-testid="create-hyperlink-btn">
            <Plus className="mr-2 h-4 w-4" />
            New Hyperlink
          </Button>
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : hyperlinks.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground border-2 border-dashed m-4 rounded-lg">
                <LinkIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No hyperlinks yet. Click &quot;New Hyperlink&quot; to add one.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead className="w-32">New Tab</TableHead>
                    <TableHead className="w-24 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hyperlinks.map((link) => (
                    <TableRow key={link.link_id} data-testid={`hyperlink-row-${link.link_id}`}>
                      <TableCell className="font-medium">{link.title}</TableCell>
                      <TableCell>
                        <a
                          href={link.url}
                          target={link.open_in_new_tab ? "_blank" : "_self"}
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline inline-flex items-center gap-1 font-mono"
                        >
                          <span className="truncate max-w-[420px] inline-block">{link.url}</span>
                          {link.open_in_new_tab && <ExternalLink className="h-3 w-3 shrink-0" />}
                        </a>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${
                          link.open_in_new_tab ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-700"
                        }`}>
                          {link.open_in_new_tab ? "Yes" : "No"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(link)}
                            data-testid={`edit-hyperlink-${link.link_id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(link)}
                            data-testid={`delete-hyperlink-${link.link_id}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Hyperlink" : "New Hyperlink"}</DialogTitle>
            <DialogDescription>
              Add or edit an external link. Title, URL, and a flag to open in a new tab.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="hl-title">Title *</Label>
              <Input
                id="hl-title"
                data-testid="hyperlink-title-input"
                placeholder="e.g., Our Rightmove listings"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hl-url">Hyperlink URL *</Label>
              <Input
                id="hl-url"
                data-testid="hyperlink-url-input"
                placeholder="https://www.rightmove.co.uk/..."
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Include the full URL with <code>https://</code>.
              </p>
            </div>
            <div className="flex items-center justify-between border rounded-md p-3">
              <div>
                <Label htmlFor="hl-newtab" className="text-sm font-medium">Open in new tab</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  When enabled, the link opens with target=&quot;_blank&quot;.
                </p>
              </div>
              <Switch
                id="hl-newtab"
                data-testid="hyperlink-newtab-switch"
                checked={formData.open_in_new_tab}
                onCheckedChange={(checked) => setFormData({ ...formData, open_in_new_tab: checked })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hl-order">Order</Label>
              <Input
                id="hl-order"
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-hyperlink-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? "Save Changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
